import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, QueryFunctionContext } from '@tanstack/react-query';
import { funcionarioService } from '../services/funcionario';
import { pracaService } from '../services/praca';
import { romaneioService } from '../services/romaneio';
import { acertoPromotorVendasService } from '../services/acertoPromotorVendas';
import { format } from 'date-fns';

const fetchRomaneios = async ({ queryKey }: QueryFunctionContext) => {
  console.log('fetchRomaneios queryFn chamada', queryKey);
  const [, idPraca] = queryKey as [string, number];
  return romaneioService.getByIdPraca(idPraca);
};

const fetchAcertoPromotorVendas = async ({ queryKey }: QueryFunctionContext) => {
  console.log('fetchAcertoPromotorVendas queryFn chamada', queryKey);
  const [, idPromotor, idPraca, idRomaneio] = queryKey as [
    string,
    number | null,
    number | null,
    number | null,
  ];
  return acertoPromotorVendasService.consultar({
    idPromotor: idPromotor || undefined,
    idPraca: idPraca || undefined,
    idRomaneio: idRomaneio || undefined,
  });
};

const AcertoPromotorVendas: React.FC = () => {
  const [idPromotor, setIdPromotor] = useState<number | null>(null);
  const [idPraca, setIdPraca] = useState<number | null>(null);
  const [idRomaneio, setIdRomaneio] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: funcionarios = [] } = useQuery({
    queryKey: ['funcionarios'],
    queryFn: funcionarioService.getAll,
  });

  const { data: pracas = [] } = useQuery({
    queryKey: ['pracas'],
    queryFn: pracaService.getAll,
  });

  const { data: romaneios = [] } = useQuery({
    queryKey: ['romaneios-acerto', idPraca],
    queryFn: fetchRomaneios,
    enabled: idPraca !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: registros = [], isLoading, isError, error } = useQuery({
    queryKey: ['acertoPromotorVendas', idPromotor, idPraca, idRomaneio],
    queryFn: fetchAcertoPromotorVendas,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const paginatedRegistros = useMemo(() => {
    return registros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [registros, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pracasFiltradas = useMemo(() => {
    if (!idPromotor) return pracas;
    return pracas.filter((p) => p.codFuncionario === idPromotor);
  }, [pracas, idPromotor]);

  const formatarData = (data?: string) => {
    if (!data) return '-';
    try {
      return format(new Date(data), 'dd/MM/yyyy');
    } catch {
      return data;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Acerto Promotor Vendas
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={funcionarios}
              getOptionLabel={(option) => option.nome || ''}
              getOptionKey={(option) => option.id}
              value={funcionarios.find((f) => f.id === idPromotor) || null}
              onChange={(_, value) => {
                setIdPromotor(value?.id || null);
                setIdPraca(null);
                setIdRomaneio(null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Promotor" variant="outlined" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={pracasFiltradas}
              getOptionLabel={(option) => option.nomePraca || ''}
              getOptionKey={(option) => option.id}
              value={pracasFiltradas.find((p) => p.id === idPraca) || null}
              onChange={(_, value) => {
                setIdPraca(value?.id || null);
                setIdRomaneio(null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Praça" variant="outlined" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={romaneios}
              getOptionLabel={(option) =>
                `#${option.id} - ${option.funcionario || ''} - ${option.dataString || ''}`
              }
              getOptionKey={(option) => option.id}
              value={romaneios.find((r) => r.id === idRomaneio) || null}
              onChange={(_, value) => {
                setIdRomaneio(value ? value.id : null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Romaneio" variant="outlined" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Promotor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Praça</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data de Saída</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data de Retorno</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Saídas Vinculadas</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Retornos no Período</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Cobrança</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Recebido</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Valor a Receber</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography>Carregando...</Typography>
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="error">
                      Erro ao carregar: {error?.message || 'Erro desconhecido'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : registros.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography>Nenhum registro encontrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRegistros.map((registro) => (
                  <TableRow key={registro.idRomaneio} hover>
                    <TableCell>{registro.nomePromotor}</TableCell>
                    <TableCell>{registro.nomePraca}</TableCell>
                    <TableCell>{formatarData(registro.dataSaida)}</TableCell>
                    <TableCell>{formatarData(registro.dataRetorno)}</TableCell>
                    <TableCell>{registro.quantidadeSaidasVinculadas}</TableCell>
                    <TableCell>{registro.quantidadeRetornosNoPeriodo}</TableCell>
                    <TableCell>
                      {Number(registro.valorTotalCobranca ?? 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </TableCell>
                    <TableCell>
                      {Number(registro.totalRecebido ?? 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </TableCell>
                    <TableCell>
                      {Number(registro.valorReceber ?? 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={registros.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} itens`}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': { pl: 2, pr: 2 }
          }}
        />
      </Paper>
    </Container>
  );
};

export default AcertoPromotorVendas;
